import { ParameterCodeDataPolicy } from "constants/common/ParameterCodeDataPolicy";
import { ChargeWaiverRegistrationEntity, EMPTY_CHARGE_WAIVER_REGISTRATION_ENTITY } from "domain/entity/ChargeWaiverRegistration/ChargeWaiverRegistrationEntity";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { ChargeWaiverRegistrationRepository } from "domain/repository/ChargeWaiverRegistration/ChargeWaiverRegistrationRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import _ from "lodash";
import { createChargeWaiverRegistrationValidationSchema } from "presentation/constant/ChargeWaiverRegistration/ChargeWaiverRegistrationValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { ChargeWaiverRegistrationModel } from "presentation/model/ChargeWaiverRegistration/ChargeWaiverRegistrationModel";
import { DropdownProps } from "presentation/model/DropdownProps";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface ChargeWaiverRegistrationVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<ChargeWaiverRegistrationModel>> | ((value: SetStateAction<ChargeWaiverRegistrationModel>) => void),
    ],
    repo: ChargeWaiverRegistrationRepository,
    chargeTypeRepo: ChargeTypeRepository,
    parameterDetailRepo: ParameterDetailRepository,
}

export const ChargeWaiverRegistrationVM = ({ dispatch, repo, chargeTypeRepo, parameterDetailRepo }: ChargeWaiverRegistrationVMProps) => {
    const [chargeWaiverRegistrationDispatch] = dispatch;

    const loadDropdownOption = async () => {
        await chargeTypeRepo.getAllChargeTypesForCombobox().then(chargeTypes => {
            let newChargeTypes = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
            // let newChargeTypes = newChargeTypeList.filter(entity => entity.chargeCategory === 'GEN' || entity.chargeCategory === 'SYS');
            let chargeTypeDropdownOption: DropdownProps[] = [];
            let subChargeTypeDropdownOption: { [key: string]: DropdownProps[] } = {};
            let chargeDescMap: { [key: string]: string } = {};
            newChargeTypes?.forEach(chgTypeEty => {
                const descKey = chgTypeEty.chargeType + "-" + (chgTypeEty.subChargeType ?? "null");
                chargeDescMap[descKey] = chgTypeEty.chargeTypeDesc ?? "";
                const chgTypeExisted = chargeTypeDropdownOption?.find(chgType =>
                    chgType.value === chgTypeEty.chargeType);
                if (!chgTypeExisted) {
                    chargeTypeDropdownOption.push({
                        dropdownLabel: chgTypeEty.chargeType,
                        tagLabel: chgTypeEty.chargeType,
                        value: chgTypeEty.chargeType
                    })
                }
                if (chgTypeEty.subChargeType) {
                    if (!subChargeTypeDropdownOption[chgTypeEty.chargeType]) {
                        subChargeTypeDropdownOption[chgTypeEty.chargeType] = [];
                    }
                    subChargeTypeDropdownOption[chgTypeEty.chargeType].push({
                        dropdownLabel: chgTypeEty.subChargeType,
                        tagLabel: chgTypeEty.subChargeType,
                        value: chgTypeEty.subChargeType
                    });
                }
            });
            chargeWaiverRegistrationDispatch(prevState => ({
                ...prevState,
                chargeDescMap: chargeDescMap,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    chargeTypeDropdownOptions: chargeTypeDropdownOption,
                    subChargeTypeDropdownOptions: subChargeTypeDropdownOption,
                }
            }))
        });

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeDataPolicy.WAIVER_REASON_CODE).then(
            waiverReasonCodes => {
                const waiverReasonCodeDropdownOption = _.sortBy(waiverReasonCodes, ['parameterDtlCode'])?.map((waiverReasonCode) => ({
                    dropdownLabel: waiverReasonCode.parameterDtlCode,
                    tagLabel: waiverReasonCode.parameterDtlCode,
                    value: waiverReasonCode.parameterDtlCode,
                })) ?? []

                chargeWaiverRegistrationDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        waiverReasonCodeDropdownOptions: waiverReasonCodeDropdownOption
                    }
                }))
            }
        )

    }

    const onSearch = async () => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                chargeWaiverRegistrationList: []
            }
        });
        await repo.getEntities().then((data) => {
            chargeWaiverRegistrationDispatch(prevState => {
                return {
                    ...prevState,
                    chargeWaiverRegistrationList: data,
                    isShowEditPanel: false,
                    isAdd: false,
                    isEdit: false,
                }
            });
        });
    }

    const onCloseEidtPanel = async () => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: { ...EMPTY_CHARGE_WAIVER_REGISTRATION_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }
            }
        });
    }

    const onSave = async (row: ChargeWaiverRegistrationEntity) => {
        const valResult = await Validation(createChargeWaiverRegistrationValidationSchema).ValidateFormOnly(row);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            chargeWaiverRegistrationDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }
        return await repo.onSave(row);
    }

    const onSaveClicked = () => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (fieldKey === "chargeType") {
            chargeWaiverRegistrationDispatch(prevState => {
                const subChgType = prevState.dynamicOptions.subChargeTypeDropdownOptions[val] ? prevState.dynamicOptions.subChargeTypeDropdownOptions[val][0].value : null;
                const deskKey = val + "-" + subChgType /*?? "null"*/;
                return {
                    ...prevState,
                    currentEditRow: {
                        ...prevState.currentEditRow,
                        [fieldKey]: val,
                        subChargeType: subChgType ?? "",
                        chargeDescription: prevState.chargeDescMap[deskKey] ?? "",
                    },
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...prevState.masterState.allFormState,
                            [fieldKey]: ''
                        },
                    }
                }
            })
        } else if (fieldKey === "subChargeType") {
            chargeWaiverRegistrationDispatch(prevState => {
                const deskKey = prevState.currentEditRow.chargeType + "-" + val;
                return {
                    ...prevState,
                    currentEditRow: {
                        ...prevState.currentEditRow,
                        [fieldKey]: val,
                        chargeDescription: prevState.chargeDescMap[deskKey] ?? "",
                    },
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...prevState.masterState.allFormState,
                            [fieldKey]: ''
                        },
                    }
                }
            })
        } else {
            chargeWaiverRegistrationDispatch(prevState => {
                return {
                    ...prevState,
                    currentEditRow: {
                        ...prevState.currentEditRow,
                        [fieldKey]: val,
                    },
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...prevState.masterState.allFormState,
                            [fieldKey]: ''
                        },
                    }
                }
            })
        }
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldName]: checked ? "Y" : "N",
                },
            }
        })
    }

    const onResetClick = async (isAdd: boolean) => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: isAdd ? { ...EMPTY_CHARGE_WAIVER_REGISTRATION_ENTITY } : prevState.currentRow,
            }
        })
    }

    const onEditClick = async () => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                isEdit: true,
            }
        })
    }

    const onRowDoubleClick = async (entity: ChargeWaiverRegistrationEntity) => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: entity,
                currentRow: entity,
                isShowEditPanel: true,
            }
        })
    }

    const onAddClick = async () => {
        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...EMPTY_CHARGE_WAIVER_REGISTRATION_ENTITY,
                    waiverReasonCode: prevState.dynamicOptions.waiverReasonCodeDropdownOptions.length > 0 ? prevState.dynamicOptions.waiverReasonCodeDropdownOptions[0].value : null,
                },
                isAdd: true,
                isShowEditPanel: true,
            }
        })
    }

    const updateSelectedRows = async (rows: ChargeWaiverRegistrationEntity[]) => {

        chargeWaiverRegistrationDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: rows,
            }
        })
    }

    return {
        onSave: onSave,
        onSearch: onSearch,
        updateSelectedRows: updateSelectedRows,
        onAddClick: onAddClick,
        onRowDoubleClick: onRowDoubleClick,
        onEditClick: onEditClick,
        onResetClick: onResetClick,
        onCheckboxChange: onCheckboxChange,
        onFieldChange: onFieldChange,
        onCloseEidtPanel: onCloseEidtPanel,
        loadDropdownOption: loadDropdownOption,
        onSaveClicked: onSaveClicked,

    }
}